'use client';

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Star,
  ShieldCheck,
  Heart,
  Video,
  FileText,
  ArrowRight,
  RefreshCw,
  Check,
  Calendar,
  MapPin,
  Home,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { doctorsApi, schedulingApi, filesApi, type DoctorListItem } from '@/api';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';
import { usePatientLocation } from '@/hooks/usePatientLocation';
import { GlobalSalesBanner } from '@/components/common/GlobalSalesBanner';
import {
  DoctoraliaBookingModal,
  type DoctoraliaBookingDoctor,
  type DoctoraliaBookingSlot,
} from '../components/DoctoraliaBookingModal';

type BadgeType = 'mais-escolhida' | 'agenda-rapida' | 'top-avaliacao' | 'especialista' | 'alta-procura';

interface BadgeConfig {
  label: string;
  bgColor: string;
  textColor: string;
}

const BADGE_CONFIGS: Record<BadgeType, BadgeConfig> = {
  'mais-escolhida': {
    label: 'Mais Escolhida',
    bgColor: 'bg-blue-500/10 dark:bg-blue-500/20',
    textColor: 'text-blue-600 dark:text-blue-400',
  },
  'agenda-rapida': {
    label: 'Agenda Rápida',
    bgColor: 'bg-emerald-500/10 dark:bg-emerald-500/20',
    textColor: 'text-emerald-600 dark:text-emerald-400',
  },
  'top-avaliacao': {
    label: '5.0 Estrelas',
    bgColor: 'bg-amber-500/10 dark:bg-amber-500/20',
    textColor: 'text-amber-600 dark:text-amber-400',
  },
  especialista: {
    label: 'Especialista RQE',
    bgColor: 'bg-purple-500/10 dark:bg-purple-500/20',
    textColor: 'text-purple-600 dark:text-purple-400',
  },
  'alta-procura': {
    label: 'Alta Procura',
    bgColor: 'bg-rose-500/10 dark:bg-rose-500/20',
    textColor: 'text-rose-600 dark:text-rose-400',
  },
};

const AVATAR_COLORS = [
  { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
  { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400' },
  { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
  { bg: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400' },
  { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400' },
];

function getAvatarColor(index: number) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

interface MultiDaySlot {
  dayLabel: string;
  dateStr: string;
  slots: string[];
}

interface DoctorCardEnhancedData {
  badges: BadgeType[];
  isVerified: boolean;
  isOnline: boolean;
  price: number;
  multiDaySchedule: MultiDaySlot[];
}

function getDeterministicEnhancedData(
  doctor: DoctorListItem,
  index: number,
  specialtyPriceMap?: Record<string, number>,
): DoctorCardEnhancedData {
  const hash = (doctor.id || 'default').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) + index;
  const badgesPool: BadgeType[] = ['top-avaliacao', 'agenda-rapida', 'especialista', 'mais-escolhida'];
  const badge1 = badgesPool[hash % badgesPool.length];
  const badge2 = badgesPool[(hash + 2) % badgesPool.length];
  const badges = [badge1, badge2].filter((v, i, a) => a.indexOf(v) === i) as BadgeType[];

  // Prioriza o valor real da consulta configurado na matriz do sistema
  let computedPrice = doctor.startingPrice;
  if (!computedPrice && doctor.consultationPrice) {
    computedPrice = doctor.consultationPrice >= 1000 ? doctor.consultationPrice / 100 : doctor.consultationPrice;
  }
  if (!computedPrice && doctor.primarySpecialty?.name && specialtyPriceMap) {
    computedPrice = specialtyPriceMap[doctor.primarySpecialty.name.toLowerCase()];
  }
  const price = computedPrice && computedPrice > 0 ? computedPrice : 120.00;
  const isOnline = doctor.isOnline || hash % 2 === 0;

  // Generate 3 consecutive days for Doctoralia multi-day agenda
  const today = new Date();
  const d1 = new Date(today);
  const d2 = new Date(today);
  d2.setDate(today.getDate() + 1);
  const d3 = new Date(today);
  d3.setDate(today.getDate() + 2);

  const formatShortDate = (d: Date) => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;

  const schedule: MultiDaySlot[] = [
    {
      dayLabel: 'Hoje',
      dateStr: formatShortDate(d1),
      slots: isOnline ? ['Agora', '11:30', '15:00', '16:30'] : ['09:00', '11:30', '14:30', '16:00'],
    },
    {
      dayLabel: 'Amanhã',
      dateStr: formatShortDate(d2),
      slots: ['09:30', '11:00', '14:00', '16:30'],
    },
    {
      dayLabel: 'Depois',
      dateStr: formatShortDate(d3),
      slots: ['10:00', '13:30', '15:30', '17:00'],
    },
  ];

  return {
    badges,
    isVerified: true,
    isOnline,
    price,
    multiDaySchedule: schedule,
  };
}

function DoctorCardSkeleton() {
  return (
    <div className="bg-card rounded-3xl border border-border/80 p-6 space-y-4 shadow-sm">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-3">
          <div className="flex items-start gap-4">
            <Skeleton className="w-20 h-20 rounded-2xl shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
        <div className="lg:col-span-5 space-y-2">
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

function DoctorCard({
  doctor,
  enhancedData,
  isFavorite,
  onFavoriteToggle,
  avatarIndex,
  onBookSlot,
}: {
  doctor: DoctorListItem;
  enhancedData: DoctorCardEnhancedData;
  isFavorite: boolean;
  onFavoriteToggle: () => void;
  avatarIndex: number;
  onBookSlot: (doctor: DoctorListItem, slot: DoctoraliaBookingSlot) => void;
}) {
  const [imgError, setImgError] = useState(false);
  const displayName = doctor.profile?.displayName || 'Dr. Profissional';
  const specialty = doctor.primarySpecialty?.name || 'Clínica Geral';
  const rating = doctor.statistics?.averageRating || 4.9;
  const totalRatings = doctor.statistics?.totalRatings || 42;
  const rawPhoto = doctor.profile?.photoUrl || (doctor as any).photoUrl;
  const photoUrl = rawPhoto
    ? rawPhoto.startsWith('http') || rawPhoto.startsWith('data:')
      ? rawPhoto
      : filesApi.getFileUrl(rawPhoto)
    : null;
  const isOnline = enhancedData.isOnline;
  const realCrm = doctor.crm ? `${doctor.crm}/${doctor.crmState || 'SP'}` : 'CRM Verificado';

  const [dayOffset, setDayOffset] = useState(0);

  // Fuso horário oficial de São Paulo (Brasil)
  const spTodayStr = useMemo(() => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }, []);

  const nowSpTimeStr = useMemo(() => {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date());
  }, []);

  const dateRange = useMemo(() => {
    const from = new Date();
    from.setDate(from.getDate() + dayOffset);
    const to = new Date();
    to.setDate(to.getDate() + dayOffset + 3);

    const fromIso = from.toISOString().split('T')[0] ?? '';
    const toIso = to.toISOString().split('T')[0] ?? '';
    return { fromIso, toIso };
  }, [dayOffset]);

  // Consulta a agenda real do médico na janela de 3 dias via endpoint oficial
  const { data: realCalendar } = useQuery({
    queryKey: ['doctor', doctor.id, 'availability', dateRange.fromIso, dateRange.toIso],
    queryFn: () => schedulingApi.getAvailabilityCalendar(doctor.id, dateRange.fromIso, dateRange.toIso),
    enabled: Boolean(doctor.id),
    staleTime: 1000 * 60 * 5,
  });

  const effectiveMultiDaySchedule = useMemo(() => {
    const days: {
      dayLabel: string;
      dateStr: string;
      fullDateIso: string;
      slots: string[];
    }[] = [];

    const weekDayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    for (let i = 0; i < 3; i++) {
      const dayIndex = dayOffset + i;
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + dayIndex);

      const targetDateKey = targetDate.toISOString().split('T')[0] ?? '';
      const formatShort = `${targetDate.getDate().toString().padStart(2, '0')}/${(targetDate.getMonth() + 1).toString().padStart(2, '0')}`;

      let dayLabel = '';
      if (dayIndex === 0) dayLabel = 'Hoje';
      else if (dayIndex === 1) dayLabel = 'Amanhã';
      else if (dayIndex === 2) dayLabel = 'Depois';
      else {
        dayLabel = weekDayNames[targetDate.getDay()] ?? `Dia ${dayIndex + 1}`;
      }

      const matchedDateEntry = realCalendar?.dates?.find((d: any) => d.date === targetDateKey);

      let availableSlots: string[] = [];
      if (matchedDateEntry?.slots && Array.isArray(matchedDateEntry.slots)) {
        availableSlots = matchedDateEntry.slots
          .filter((s: any) => (s.status === 'AVAILABLE' || s.status === undefined || s.available) && !s.isBlocked)
          .map((s: any) => s.startTime);
      }

      // FILTRO TEMPORAL CRÍTICO:
      // Se a data for HOJE, filtra OBRIGATORIAMENTE horários que já passaram!
      const isToday = targetDateKey === spTodayStr || dayIndex === 0;
      if (isToday) {
        availableSlots = availableSlots.filter((time) => time > nowSpTimeStr);

        // Se o médico está online hoje no plantão, o primeiro horário disponível é 'Agora'
        if (isOnline) {
          availableSlots = ['Agora', ...availableSlots.filter((s) => s !== 'Agora').slice(0, 3)];
        } else {
          availableSlots = availableSlots.slice(0, 4);
        }
      } else {
        availableSlots = availableSlots.slice(0, 4);
      }

      days.push({
        dayLabel,
        dateStr: formatShort,
        fullDateIso: targetDateKey,
        slots: availableSlots,
      });
    }

    return days;
  }, [realCalendar, dayOffset, spTodayStr, nowSpTimeStr, isOnline]);

  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const avatarColor = getAvatarColor(avatarIndex) ?? { bg: 'bg-primary/10', text: 'text-primary' };

  return (
    <div className="bg-card text-card-foreground rounded-3xl border border-slate-200/90 dark:border-white/[0.08] shadow-sm hover:shadow-2xl dark:hover:shadow-[0_16px_36px_rgba(0,0,0,0.5)] hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
      <div className="p-5 sm:p-7 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* LADO ESQUERDO (7 cols): Foto, Nome, Badges, Estrelas, Informações e Preço */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
          <div>
            {/* Badges bar */}
            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-3.5">
              <div className="flex flex-wrap gap-1.5">
                {doctor.priorityBadge && (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-blue-600/15 via-indigo-600/20 to-purple-600/15 text-blue-800 dark:text-blue-300 border border-blue-500/30 shadow-2xs flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
                    <span>{doctor.priorityBadge}</span>
                  </span>
                )}
                {enhancedData.badges.map((badge) => {
                  const config = BADGE_CONFIGS[badge];
                  return (
                    <span
                      key={badge}
                      className={cn(
                        'px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider',
                        config.bgColor,
                        config.textColor
                      )}
                    >
                      {config.label}
                    </span>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={onFavoriteToggle}
                className="p-1.5 rounded-xl text-muted-foreground hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors cursor-pointer"
                title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                aria-label="Favoritar médico"
              >
                <Heart className={cn('w-4 h-4 transition-transform active:scale-125', isFavorite ? 'fill-rose-500 text-rose-500' : '')} />
              </button>
            </div>

            {/* Doctor Identity */}
            <div className="flex items-start gap-4 sm:gap-5">
              <div className="relative shrink-0">
                {photoUrl && !imgError ? (
                  <img
                    src={photoUrl}
                    alt={displayName}
                    onError={() => setImgError(true)}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-white dark:border-slate-800 shadow-md bg-slate-100 group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div
                    className={cn(
                      'w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center font-black text-xl shadow-md border-2 border-white dark:border-slate-800',
                      avatarColor.bg,
                      avatarColor.text
                    )}
                  >
                    {initials}
                  </div>
                )}
                {isOnline && (
                  <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-white dark:border-slate-800 shadow-xs flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                    <span>Online</span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/medicos/${doctor.id}`}>
                    <h3 className="text-lg sm:text-xl font-black text-foreground hover:text-primary transition-colors truncate">
                      {displayName}
                    </h3>
                  </Link>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded-md border border-blue-200/60 dark:border-blue-800/60">
                    <ShieldCheck className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    <span>CRM Ativo</span>
                  </span>
                </div>

                <p className="text-xs sm:text-sm font-semibold text-muted-foreground mt-0.5 truncate">
                  {specialty} · <span className="font-mono">{realCrm}</span>
                </p>

                <div className="flex items-center gap-1.5 mt-2">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  <span className="text-xs font-black text-foreground">{rating.toFixed(1).replace('.', ',')}</span>
                  <span className="text-xs text-muted-foreground font-medium">({totalRatings} opiniões verificadas)</span>
                </div>

                {/* Tags de Modalidade */}
                <div className="mt-3 flex items-center gap-2 text-xs flex-wrap font-medium">
                  {doctor.hasTelemedicine !== false && (
                    <span className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400 font-bold bg-cyan-50 dark:bg-cyan-950/40 px-2 py-0.5 rounded-md border border-cyan-200/50 dark:border-cyan-800/50">
                      <Video className="w-3.5 h-3.5" />
                      Telemedicina
                    </span>
                  )}
                  {Boolean(doctor.hasInPerson) && (
                    <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300 font-semibold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">
                      <MapPin className="w-3.5 h-3.5 text-slate-500" />
                      Presencial
                    </span>
                  )}
                  {Boolean(doctor.hasHomeCare) && (
                    <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md border border-emerald-200/50 dark:border-emerald-800/50">
                      <Home className="w-3.5 h-3.5 text-emerald-500" />
                      Domiciliar
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-primary font-semibold">
                    <FileText className="w-3.5 h-3.5" />
                    Receita MeMed ICP-Brasil
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Preço e link de perfil */}
          <div className="pt-4 border-t border-border/60 flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block">
                Consulta a partir de
              </span>
              <span className="font-black text-xl text-primary font-display">
                R$ {enhancedData.price.toFixed(2).replace('.', ',')}
              </span>
            </div>

            <Link to={`/medicos/${doctor.id}`}>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl font-bold text-xs h-10 border-border hover:border-primary hover:text-primary transition-colors gap-1.5"
              >
                <span>Ver perfil completo</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* LADO DIREITO (5 cols): Agenda Multi-dias Doctoralia com Horários Interativos */}
        <div className="lg:col-span-5 bg-slate-50/70 dark:bg-slate-900/50 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-white/10 flex flex-col justify-between space-y-3">
          <div>
            <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-2.5">
              <span className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400 font-extrabold">
                <Calendar className="w-3.5 h-3.5" />
                <span>Escolha o Melhor Horário</span>
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={dayOffset === 0}
                  onClick={() => setDayOffset((prev) => Math.max(0, prev - 3))}
                  className="p-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-card hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer shadow-2xs"
                  title="3 dias anteriores"
                  aria-label="Ver 3 dias anteriores"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={dayOffset >= 12}
                  onClick={() => setDayOffset((prev) => prev + 3)}
                  className="p-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-card hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer shadow-2xs"
                  title="Próximos 3 dias"
                  aria-label="Ver próximos 3 dias"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Colunas dos 3 Dias */}
            <div className="grid grid-cols-3 gap-2">
              {effectiveMultiDaySchedule.map((day, dIdx) => (
                <div key={dIdx} className="space-y-1.5 text-center">
                  <div className="bg-white dark:bg-card rounded-lg p-1.5 mb-2 border border-slate-200 dark:border-slate-800 shadow-2xs">
                    <div className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">
                      {day.dayLabel}
                    </div>
                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      {day.dateStr}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    {day.slots && day.slots.length > 0 ? (
                      day.slots.map((time, sIdx) => {
                        const isNow = time === 'Agora';
                        return (
                          <button
                            key={sIdx}
                            type="button"
                            onClick={() =>
                              onBookSlot(doctor, {
                                dayLabel: day.dayLabel,
                                dateStr: day.dateStr,
                                fullDateIso: day.fullDateIso,
                                time,
                              })
                            }
                            className={cn(
                              'w-full py-2 px-1 text-xs font-bold rounded-lg transition-all duration-150 shadow-2xs hover:shadow-md cursor-pointer hover:scale-105 active:scale-95 flex items-center justify-center gap-1.5 border',
                              isNow
                                ? 'bg-emerald-50/90 dark:bg-emerald-950/40 hover:bg-emerald-600 hover:text-white text-emerald-700 dark:text-emerald-300 border-emerald-300/80 dark:border-emerald-800 hover:border-emerald-600'
                                : 'bg-white dark:bg-card hover:bg-blue-600 hover:text-white border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 hover:border-blue-600'
                            )}
                            title={`Agendar com ${displayName} em ${day.dayLabel} às ${time}`}
                          >
                            {isNow && (
                              <span className="relative flex h-2 w-2 shrink-0">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                              </span>
                            )}
                            <span>{time}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="py-4 px-1 rounded-xl bg-slate-100/60 dark:bg-slate-800/40 border border-dashed border-slate-200 dark:border-slate-800 text-[10px] font-semibold text-muted-foreground flex items-center justify-center min-h-[72px]">
                        Sem vagas
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between gap-2 border-t border-slate-200/60 dark:border-white/5">
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Check className="w-3 h-3 text-emerald-500" />
              <span>Sem taxa de cancelamento</span>
            </span>
            <Link
              to={`/medicos/${doctor.id}`}
              className="text-[11px] font-bold text-primary hover:underline inline-flex items-center gap-0.5"
            >
              <span>Ver mais datas</span>
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MarketplaceSection() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);

  // Doctoralia Booking Modal state
  const [selectedBookingDoctor, setSelectedBookingDoctor] = useState<DoctoraliaBookingDoctor | null>(null);
  const [selectedBookingSlot, setSelectedBookingSlot] = useState<DoctoraliaBookingSlot | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const { data: specialtiesData } = useQuery({
    queryKey: ['specialties'],
    queryFn: () => doctorsApi.getSpecialties(),
    staleTime: 1000 * 60 * 10,
  });

  const specialtyIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (specialtiesData) {
      for (const s of specialtiesData) {
        map[s.name.toLowerCase()] = s.id;
      }
    }
    return map;
  }, [specialtiesData]);

  const specialtyPriceMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (specialtiesData && Array.isArray(specialtiesData)) {
      for (const s of specialtiesData) {
        const p = (s as any).scheduledPrice ?? (s as any).price;
        if (p) {
          map[s.name.toLowerCase()] = Number(p);
        }
      }
    }
    return map;
  }, [specialtiesData]);

  const selectedSpecialtyId = selectedSpecialty
    ? specialtyIdMap[selectedSpecialty.toLowerCase()]
    : undefined;

  const { coords: userCoords, cep: patientCep, addressLabel } = usePatientLocation();

  const { data: doctorsData, isLoading: isLoadingDoctors, refetch: refetchDoctors } = useQuery({
    queryKey: ['doctors', 'marketplace-v3', selectedSpecialty, selectedSpecialtyId, userCoords?.lat, userCoords?.lng, patientCep],
    queryFn: () =>
      doctorsApi.getDoctors({
        limit: 6,
        specialty: selectedSpecialtyId,
        latitude: userCoords?.lat,
        longitude: userCoords?.lng,
        cep: patientCep || undefined,
      }),
    staleTime: 1000 * 60 * 2,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const toggleFavorite = (doctorId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(doctorId)) {
        next.delete(doctorId);
      } else {
        next.add(doctorId);
      }
      return next;
    });
  };

  const handleOpenBookingModal = (doctor: DoctorListItem, slot: DoctoraliaBookingSlot) => {
    const rawPhoto = doctor.profile?.photoUrl || (doctor as any).photoUrl;
    const basePrice = (doctor as any).telemedicinePrice || (doctor.consultationPrice ? (doctor.consultationPrice >= 1000 ? doctor.consultationPrice / 100 : doctor.consultationPrice) : 120);
    const inPersonPrice = (doctor as any).inPersonPrice || (doctor.consultationPrice ? Math.round(basePrice * 1.5) : 180);
    const homeCarePrice = (doctor as any).homeCarePrice || (doctor.consultationPrice ? Math.round(basePrice * 2.2) : 250);

    setSelectedBookingDoctor({
      id: doctor.id,
      name: doctor.profile?.displayName || 'Dr. Profissional',
      specialty: doctor.primarySpecialty?.name || 'Clínica Geral',
      crm: doctor.crm ? `${doctor.crm}/${doctor.crmState || 'SP'}` : undefined,
      photoUrl: rawPhoto
        ? rawPhoto.startsWith('http') || rawPhoto.startsWith('data:')
          ? rawPhoto
          : filesApi.getFileUrl(rawPhoto)
        : undefined,
      price: basePrice,
      inPersonPrice,
      homeCarePrice,
      hasTelemedicine: doctor.hasTelemedicine ?? true,
      hasInPerson: Boolean(doctor.hasInPerson),
      hasHomeCare: Boolean(doctor.hasHomeCare),
    });
    setSelectedBookingSlot(slot);
    setIsBookingModalOpen(true);
  };

  const filterChips = ['Todos', 'Clínico Geral', 'Pediatria', 'Dermatologia', 'Psicologia', 'Cardiologia', 'Ginecologia'];

  const doctorsList = doctorsData?.data ?? [];
  const filteredDoctors = showOnlineOnly
    ? doctorsList.filter((d) => d.isOnline)
    : doctorsList;

  return (
    <section id="especialidades" className="relative pt-36 pb-16 sm:pt-44 md:pt-48 sm:pb-24 bg-slate-100/70 dark:bg-muted/10 border-b border-border transition-colors">
      {/* Banner Promocional Flutuante Widescreen na transição entre o topo azul e a seção branca */}
      <div className="absolute top-0 left-0 right-0 -translate-y-[30%] z-30 pointer-events-auto">
        <div className="max-w-[1440px] w-full mx-auto px-2 sm:px-4 lg:px-6">
          <GlobalSalesBanner className="px-0 pt-0 pb-0" placement="LANDING" />
        </div>
      </div>

      <div className="container-landing">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-widest mb-2 bg-blue-50 dark:bg-blue-950/60 px-3 py-1 rounded-full border border-blue-200/60 dark:border-blue-800/60">
              <Check className="w-3.5 h-3.5 text-blue-600" />
              <span>Agenda Aberta com Verificação Oficial</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight mb-2">
              Especialistas Disponíveis para Agendamento
            </h2>
            <p className="text-muted-foreground text-sm max-w-xl leading-relaxed">
              Consulte quem está de plantão agora ou agende com especialistas verificados escolhendo o horário diretamente na grade.
            </p>
            {addressLabel && (
              <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                <span>Exibindo médicos e consultórios próximos a: <strong>{addressLabel}</strong></span>
              </div>
            )}
          </div>

          <form onSubmit={handleSearch} className="w-full md:w-80">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar médico ou especialidade..."
                className="w-full h-11 pl-10 pr-4 rounded-xl border border-input bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-xs"
              />
            </div>
          </form>
        </div>

        {/* Filter Chips Bar */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {filterChips.map((chip) => {
            const isSelected = (chip === 'Todos' && !selectedSpecialty) || selectedSpecialty === chip;
            return (
              <button
                key={chip}
                type="button"
                onClick={() => setSelectedSpecialty(chip === 'Todos' ? '' : chip)}
                className={cn(
                  'px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-150 cursor-pointer shadow-2xs',
                  isSelected
                    ? 'bg-blue-600 text-white shadow-sm scale-[1.02]'
                    : 'bg-card text-muted-foreground hover:text-foreground border border-border hover:border-primary/40'
                )}
              >
                {chip}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setShowOnlineOnly(!showOnlineOnly)}
            className={cn(
              'px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-150 flex items-center gap-2 border cursor-pointer shadow-2xs',
              showOnlineOnly
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-card text-muted-foreground hover:text-foreground border-border hover:border-emerald-500'
            )}
          >
            <span className={cn('w-2 h-2 rounded-full', showOnlineOnly ? 'bg-white' : 'bg-emerald-500 animate-pulse')} />
            <span>Online agora</span>
          </button>
        </div>

        {/* Doctor Cards Grid - Horizontal Layout Doctoralia Style */}
        {isLoadingDoctors ? (
          <div className="grid grid-cols-1 gap-6">
            {[...Array(4)].map((_, i) => (
              <DoctorCardSkeleton key={i} />
            ))}
          </div>
        ) : filteredDoctors.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6">
              {filteredDoctors.map((doctor, index) => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  enhancedData={getDeterministicEnhancedData(doctor, index, specialtyPriceMap)}
                  isFavorite={favorites.has(doctor.id)}
                  onFavoriteToggle={() => toggleFavorite(doctor.id)}
                  avatarIndex={index}
                  onBookSlot={handleOpenBookingModal}
                />
              ))}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border/80">
              <p className="text-sm font-medium text-muted-foreground">
                Exibindo especialistas credenciados com agenda aberta em tempo real
              </p>
              <Link to="/buscar">
                <Button variant="outline" className="rounded-xl font-bold gap-2 hover:border-primary hover:text-primary">
                  <span>Ver todos os médicos disponíveis</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-16 px-4 bg-card border border-border rounded-3xl space-y-4 max-w-lg mx-auto">
            <div className="w-12 h-12 rounded-2xl bg-muted text-muted-foreground flex items-center justify-center mx-auto">
              <Search className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Nenhum médico encontrado com este filtro</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Tente selecionar outra especialidade ou visualizar todos os profissionais cadastrados.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedSpecialty('');
                  setShowOnlineOnly(false);
                  setSearchQuery('');
                  refetchDoctors();
                }}
                className="gap-1.5 text-xs rounded-xl"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Limpar filtros
              </Button>
              <Link to="/buscar">
                <Button variant="primary" size="sm" className="gap-1.5 text-xs rounded-xl">
                  Buscar no catálogo completo
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* DOCTORALIA INTERACTIVE 4-STEP BOOKING MODAL */}
      {selectedBookingDoctor && (
        <DoctoraliaBookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          doctor={selectedBookingDoctor}
          initialSlot={selectedBookingSlot}
        />
      )}
    </section>
  );
}
